import React from 'react';
import Icon from '../AppIcon';

// Elementi comuni delle dashboard statistiche (stesso aspetto delle dashboard CCO).

// Palette ben contrastata, leggibile su sfondo chiaro e scuro
export const PALETTE = [
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

/** Colore stabile di una categoria: dipende dalla sua posizione nell'elenco completo. */
export const coloreCategoria = (valore, elencoCompleto) => {
  if (valore === undefined || valore === null || valore === '') return '#94a3b8';
  const i = elencoCompleto.indexOf(valore);
  return i < 0 ? '#94a3b8' : PALETTE[i % PALETTE.length];
};

export const AXIS_STYLE = { fontSize: 9, fontWeight: 700, fill: 'rgba(128,128,128,0.5)' };
export const GRID_PROPS = { strokeDasharray: '3 3', stroke: 'rgba(128,128,128,0.07)', vertical: false };

export const fmt = (n) => Number(n).toLocaleString('it-IT');

export const MESI_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

/** "03/2026" -> "Mar '26" */
export const meseBreve = (mmyyyy) => {
  if (!mmyyyy) return '';
  const [mm, yyyy] = mmyyyy.split('/');
  return `${MESI_IT[parseInt(mm, 10) - 1]} '${yyyy.slice(2)}`;
};

// ─── Tooltip dei grafici ──────────────────────────────────────────────────────
export const GraficoTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const ordinati = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
  const totale = ordinati.reduce((s, p) => s + (p.value || 0), 0);

  return (
    <div className="bg-card border border-border/60 rounded-2xl shadow-2xl px-4 py-3.5 min-w-[170px] max-w-[280px] pointer-events-none">
      {label !== undefined && label !== null && (
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2.5 pb-2 border-b border-border/20 break-words">
          {label}
        </p>
      )}
      {ordinati.map((p) => (
        <div key={p.dataKey ?? p.name} className="flex items-center justify-between gap-4 mb-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color ?? p.fill ?? p.payload?.fill }} />
            <span className="text-[10px] font-bold text-muted-foreground/70 truncate">{p.name}</span>
          </div>
          <span className="text-[12px] font-black text-foreground tabular-nums">{fmt(p.value)}</span>
        </div>
      ))}
      {ordinati.length > 1 && (
        <div className="flex justify-between pt-2 mt-1 border-t border-border/20">
          <span className="text-[9px] font-black uppercase text-muted-foreground/40">Totale</span>
          <span className="text-[12px] font-black text-primary tabular-nums">{fmt(totale)}</span>
        </div>
      )}
    </div>
  );
};

// ─── Scheda KPI ───────────────────────────────────────────────────────────────
export const KpiCard = ({ label, value, sub, icon, color = '#6366f1', trend }) => (
  <div className="relative flex flex-col gap-3 p-5 rounded-3xl bg-card border border-border/40 overflow-hidden group hover:border-border/60 transition-colors">
    <div
      className="absolute inset-0 rounded-3xl transition-opacity opacity-0 group-hover:opacity-100"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}10, transparent 60%)` }}
    />
    <div className="flex items-center justify-between relative z-10">
      <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">{label}</span>
      <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
        <Icon name={icon} size={14} style={{ color }} />
      </div>
    </div>
    <div className="flex items-end justify-between relative z-10 gap-2">
      <span className="text-3xl font-black tracking-tighter leading-none break-words min-w-0" style={{ color }}>{value ?? '—'}</span>
      {trend !== null && trend !== undefined && (
        <span className={`text-[10px] font-black flex items-center gap-0.5 mb-0.5 shrink-0 ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
          <Icon name={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} size={11} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    {sub && <span className="text-[9px] font-bold text-muted-foreground/30 uppercase tracking-widest relative z-10 leading-tight">{sub}</span>}
  </div>
);

// ─── Scheda grafico ───────────────────────────────────────────────────────────
export const ChartCard = ({ title, subtitle, contextLabel, icon, children, actions }) => (
  <div className="flex flex-col gap-5 p-6 rounded-3xl bg-muted/[0.06] border border-border/30 hover:border-border/50 transition-colors min-w-0">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex items-start gap-3">
        {icon && (
          <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0 mt-0.5">
            <Icon name={icon} size={13} className="text-primary/70" />
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          <p className="text-[12px] font-black uppercase tracking-widest text-foreground/80">{title}</p>
          {subtitle && <p className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-wider">{subtitle}</p>}
          {contextLabel && (
            <div className="flex items-center gap-1.5 mt-1">
              <div className="w-1 h-1 rounded-full bg-primary/50" />
              <span className="text-[9px] font-black text-primary/60 uppercase tracking-widest">{contextLabel}</span>
            </div>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
    {children}
  </div>
);

// ─── Selettore del tipo di grafico ────────────────────────────────────────────
export const ChartToggle = ({ value, onChange, options }) => (
  <div className="flex gap-0.5 bg-muted/50 border border-border/30 p-0.5 rounded-xl">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all duration-150 ${
          value === o.value
            ? 'bg-card shadow text-foreground border border-border/40'
            : 'text-muted-foreground/40 hover:text-muted-foreground'
        }`}
      >
        <Icon name={o.icon} size={10} />
        {o.label}
      </button>
    ))}
  </div>
);

// ─── Gruppo di filtri a "pill" (una sola scelta) ──────────────────────────────
/** options: [{ value, label }]; il valore 'TUTTO' = nessun filtro */
export const FilterGroup = ({ label, options, value, onChange, color = '#6366f1' }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/30 shrink-0 min-w-[40px]">{label}</span>
    <div className="flex gap-1.5 flex-wrap">
      {options.map((o) => {
        const attivo = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-150 border ${
              attivo
                ? 'text-white border-transparent shadow-sm'
                : 'bg-muted/20 border-border/25 text-muted-foreground/50 hover:text-foreground hover:border-border/50'
            }`}
            style={attivo ? { background: color } : {}}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

// ─── Legenda a "pill" con totale ──────────────────────────────────────────────
/** voci: [{ chiave, label, colore, totale? }] */
export const LegendaPills = ({ voci }) => (
  <div className="flex flex-wrap gap-2 pt-1 border-t border-border/10">
    {voci.map((v) => (
      <div
        key={v.chiave}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl border"
        style={{ background: `${v.colore}12`, borderColor: `${v.colore}30` }}
      >
        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: v.colore }} />
        <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: v.colore }}>
          {v.label}{v.totale !== undefined && v.totale !== null ? ` · ${fmt(v.totale)}` : ''}
        </span>
      </div>
    ))}
  </div>
);
